import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

// Shown in the RESULT area when a CoPilot run completes with ZERO solutions (outcome 'empty') — a valid
// "no suggestion", NOT a failure (owner 2026-06-22). Purely presentational; the settings stay collapsed.
//
// The REASON is the point (CR-20260724-001). The engine says exactly why it had nothing — a shortfall it
// is not permitted to reach, no priceable structure, the position already meeting the request — and that
// sentence rides to the panel on the run's failureDetail. It used to be dropped here in favour of a fixed
// line, which left "no suggestions" indistinguishable from "no suggestions exist" and forced a log dive
// for something the run had already answered.
@Component({
    selector: 'ets-hg-copilot-empty-result',
    template: `
        <div class="empty-result">
            <i class="fas fa-info-circle"></i>
            <div class="title">{{ this.heading }}</div>
            <div class="msg">{{ this.message }}</div>
        </div>
    `,
    styles: [`
        :host { display: block; }
        .empty-result {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 4px;
            padding: 16px 12px;
            border-radius: 4px;
            background: rgba(255, 255, 255, 0.06);
        }
        .empty-result i { font-size: 20px; opacity: 0.6; }
        .empty-result .title { font-weight: 600; }
        .empty-result .msg { font-size: 12px; opacity: 0.75; }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HgCopilotEmptyResultComponent {

    // The producer's own explanation. Null on paths that supply none (the LLM skill, a locally
    // generated Close with nothing to close) — those fall back to the generic line below.
    @Input() reason: string | null = null;

    // WHY THIS EXISTS (CR-20260806-001 follow-on, owner 2026-08-07): the result filters can hide
    // every package, which empties the results area exactly as a no-suggestion run does. Same box,
    // two different facts — the engine found nothing, versus the trader filtered everything out —
    // and only the heading separates them. Defaulted to the original wording so the run path is
    // untouched; the filter path passes its own.
    //
    // ⚠ Named `heading`, NOT `title`: `title` is a global HTML attribute, so `[title]` on this
    // selector reads as a browser tooltip to everyone who has not checked whether an @Input of
    // that name exists.
    @Input() heading = 'No Suggestions';

    private static readonly GENERIC =
        'ORION Ran Successfully But Returned no Solutions For These Inputs. Adjust Your Inputs And Run Again.';

    get message(): string {
        const reason = (this.reason || '').trim();
        return reason.length > 0 ? reason : HgCopilotEmptyResultComponent.GENERIC;
    }
}
